/**
 * API Lot Operations
 *
 * Handles CRUD operations for lots
 * @module portal/modules/api/lots
 */

import { state } from '../state.js';
import { retryWithBackoff } from '../../../shared/retry.js';
import { createLogger } from '../../../shared/logger.js';
import { apiUrl, resolveGlobalConfig, type ApiConfig } from './config.js';
import { ensureAccessToken } from './auth.js';

const log = createLogger('portal:api:lots');
const DEBUG = false;

interface PortalLot {
  id?: string;
  title?: string;
  url?: string;
  source?: string;
  currentBid?: number;
  nextRequiredBid?: number;
  closeAt?: number;
  notes?: string;
  status?: string;
}

interface RemoteLotsSuccess {
  success: true;
  data: unknown[];
  /** Server timestamp for delta sync - store and send as 'since' on next poll */
  serverTime?: string;
  /** When true, response contains only changed items since 'since' timestamp */
  isDelta?: boolean;
}

interface RemoteLotsFailure {
  success: false;
  error: string;
}

type RemoteLotsResult = RemoteLotsSuccess | RemoteLotsFailure;

/**
 * Options for fetching remote lots
 */
interface FetchRemoteLotsOptions {
  /** User ID to filter by (superadmin impersonation) */
  onlyUserId?: string | null;
  /** ISO 8601 timestamp for delta sync - only fetch lots updated since this time */
  since?: string;
}

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'An unknown error occurred';
}

async function postWithJson(
  url: string,
  token: string,
  data: Record<string, unknown>
): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
    },
    body: JSON.stringify(data),
  });
}

/**
 * Fetch remote lots from API
 * @param cfg - API configuration
 * @param options - Fetch options including userId filter and delta sync timestamp
 */
export async function fetchRemoteLots(
  cfg?: ApiConfig | null,
  options: FetchRemoteLotsOptions | string | null = null
): Promise<RemoteLotsResult> {
  // Handle legacy signature: fetchRemoteLots(cfg, onlyUserId)
  const opts: FetchRemoteLotsOptions =
    typeof options === 'string' || options === null ? { onlyUserId: options } : options;

  if (state.mode !== 'auth0') {
    if (DEBUG) {
      log.debug('Skipping remote fetch in local mode');
    }
    return { success: false, error: 'Not in auth0 mode' };
  }

  if (state.isFetchingRemote) {
    if (DEBUG) {
      log.debug('Fetch already in progress, skipping');
    }
    return { success: false, error: 'Fetch in progress' };
  }

  state.isFetchingRemote = true;
  state.remoteError = '';
  const resolvedCfg = cfg ?? resolveGlobalConfig();

  try {
    let currentToken = await ensureAccessToken(resolvedCfg);
    if (!currentToken) {
      throw new Error('Unable to acquire access token.');
    }

    let retryAuth = false;

    // Use shared retry utility for transient failures
    const result = await retryWithBackoff(
      async () => {
        const queryParams: string[] = [];

        // Check if we're in superadmin view mode
        const isSuperUser = !!(state.user && (state.user as any).isSuperUser);
        const isSuperAdminView = state.viewMode === 'superadmin';

        // Add scope parameter - global for superadmin, user for regular users
        if (isSuperUser && isSuperAdminView) {
          queryParams.push('scope=global');
          queryParams.push('includeDeleted=true');
        }

        if (opts.onlyUserId) {
          queryParams.push('userId=' + encodeURIComponent(opts.onlyUserId));
        }

        // Delta sync: add 'since' parameter if provided
        if (opts.since) {
          queryParams.push('since=' + encodeURIComponent(opts.since));
        }

        const query = queryParams.length ? queryParams.join('&') : '';
        const endpoint = apiUrl(resolvedCfg, 'extensions/scrapes') + (query ? '?' + query : '');

        if (DEBUG) {
          log.debug('Fetching remote lots from', endpoint, opts.since ? '(delta)' : '(full)');
        }

        const response = await fetch(endpoint, {
          headers: { Authorization: 'Bearer ' + String(currentToken) },
        });

        if (response.status === 401 && !retryAuth) {
          state.extAccessToken = null;
          currentToken = await ensureAccessToken(resolvedCfg, true);
          retryAuth = true;
          throw new Error('AUTH_RETRY'); // Signal auth retry
        }

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(text || 'Failed to fetch lots (' + response.status + ')');
        }

        const data = await response.json();
        // API returns { items: [...], continuationToken?: string, serverTime?: string, isDelta?: boolean }
        const items = data?.items || (Array.isArray(data) ? data : []);
        if (DEBUG) {
          log.debug('Received', items.length, 'lots', data?.isDelta ? '(delta)' : '(full)');
        }

        return {
          success: true,
          data: items,
          serverTime: data?.serverTime,
          isDelta: data?.isDelta ?? Boolean(opts.since),
        };
      },
      {
        maxAttempts: 1,
        baseDelayMs: 100,
        isRetryable: error => {
          if (error instanceof Error && error.message === 'AUTH_RETRY') {
            return true;
          }
          // Retry on network errors
          return error instanceof TypeError;
        },
      }
    );

    return result as RemoteLotsResult;
  } catch (error) {
    const message = getErrorMessage(error);
    log.error('Error fetching remote lots:', message);
    state.remoteError = message;
    return { success: false, error: message };
  } finally {
    state.isFetchingRemote = false;
  }
}

/**
 * Refresh lot bids from API
 */
export async function refreshLotBids(
  cfg?: ApiConfig | null,
  id?: string,
  retry = false
): Promise<unknown> {
  if (!id) {
    throw new Error('Lot id is required to refresh bids.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg, Boolean(retry));
  if (!token) {
    throw new Error('Unable to acquire access token.');
  }

  const endpoint = apiUrl(
    resolvedCfg,
    'extensions/scrapes/' + encodeURIComponent(id) + '/refresh-bids'
  );
  if (DEBUG) {
    log.debug('POST', endpoint);
  }

  const response = await postWithJson(endpoint, token, {});

  if (DEBUG) {
    log.debug('POST status', response.status);
  }

  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return refreshLotBids(resolvedCfg, id, true);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to refresh bids (' + response.status + ')');
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType && !contentType.includes('json')) {
    throw new Error('Unexpected response from API.');
  }

  return await response.json();
}

/**
 * Result entry for a single lot in a batch bid refresh.
 */
export interface BatchBidRefreshEntry {
  id: string;
  ok: boolean;
  doc?: unknown;
  error?: string;
}

/**
 * Refresh bids for multiple lots in a single API call.
 * Throws an error named 'BatchUnsupportedError' when the API does not
 * expose the batch endpoint yet (deploy skew), so callers can fall back
 * to per-lot requests.
 */
export async function refreshLotBidsBatch(
  cfg?: ApiConfig | null,
  ids?: string[],
  retry = false
): Promise<BatchBidRefreshEntry[]> {
  if (!ids || ids.length === 0) {
    throw new Error('Lot ids are required to refresh bids.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg, Boolean(retry));
  if (!token) {
    throw new Error('Unable to acquire access token.');
  }

  const endpoint = apiUrl(resolvedCfg, 'extensions/scrapes/refresh-bids');
  if (DEBUG) {
    log.debug('POST', endpoint, ids.length, 'lots');
  }

  const response = await postWithJson(endpoint, token, { ids });

  if (DEBUG) {
    log.debug('POST status', response.status);
  }

  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return refreshLotBidsBatch(resolvedCfg, ids, true);
  }

  if (response.status === 404) {
    const err = new Error('Batch bid refresh endpoint unavailable');
    err.name = 'BatchUnsupportedError';
    throw err;
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to refresh bids (' + response.status + ')');
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType && !contentType.includes('json')) {
    throw new Error('Unexpected response from API.');
  }

  const body = (await response.json()) as { results?: BatchBidRefreshEntry[] };
  return Array.isArray(body?.results) ? body.results : [];
}

/**
 * Refresh auctioneer logo from API
 */
export async function refreshAuctioneerLogo(
  cfg?: ApiConfig | null,
  id?: string,
  retry = false
): Promise<unknown> {
  if (!id) {
    throw new Error('Lot id is required to refresh auctioneer logo.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg, Boolean(retry));
  if (!token) {
    throw new Error('Unable to acquire access token.');
  }

  const endpoint = apiUrl(
    resolvedCfg,
    'extensions/scrapes/' + encodeURIComponent(id) + '/refresh-auctioneer-logo'
  );
  if (DEBUG) {
    log.debug('POST', endpoint);
  }

  const response = await postWithJson(endpoint, token, {});

  if (DEBUG) {
    log.debug('POST status', response.status);
  }

  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return refreshAuctioneerLogo(resolvedCfg, id, true);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to refresh auctioneer logo (' + response.status + ')');
  }

  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  if (contentType && !contentType.includes('json')) {
    throw new Error('Unexpected response from API.');
  }

  return await response.json();
}

/**
 * Submit lot to API
 */
export async function submitLot(
  cfg?: ApiConfig | null,
  lot?: PortalLot,
  retry = false
): Promise<unknown> {
  if (!lot) {
    throw new Error('Lot data is required to submit.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg, Boolean(retry));
  if (!token) {
    throw new Error('Unable to acquire access token.');
  }

  const payload = {
    title: lot.title,
    url: lot.url,
    source: lot.source,
    currentBid: lot.currentBid,
    nextRequiredBid: lot.nextRequiredBid,
    closeAt: lot.closeAt,
    description: lot.notes,
  };

  const body = { payload, status: lot.status || 'received' };
  const response = await postWithJson(apiUrl(resolvedCfg, 'extensions/scrapes'), token, body);

  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return submitLot(resolvedCfg, lot, true);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to submit lot (' + response.status + ')');
  }

  return await response.json();
}

/**
 * Delete lot from API
 */
export async function deleteLot(
  cfg?: ApiConfig | null,
  id?: string,
  retry = false
): Promise<boolean> {
  if (!id) {
    throw new Error('Lot id is required to delete.');
  }
  const resolvedCfg = cfg ?? resolveGlobalConfig();
  const token = await ensureAccessToken(resolvedCfg, Boolean(retry));
  if (!token) {
    throw new Error('Unable to acquire access token.');
  }

  const endpoint = apiUrl(resolvedCfg, 'extensions/scrapes/' + encodeURIComponent(id));
  const response = await fetch(endpoint, {
    method: 'DELETE',
    headers: { Authorization: 'Bearer ' + String(token) },
  });

  if (response.status === 401 && !retry) {
    state.extAccessToken = null;
    return deleteLot(resolvedCfg, id, true);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || 'Failed to delete lot (' + response.status + ')');
  }

  return true;
}
